// Precondition: Child components (chat-input, chat-message, chat-suggestions, chat-tool-result) loaded
// Postcondition: Full chat interface with streaming LLM responses and tool execution
// Invariants: Messages array maintained, status reflects current state

import { createLogger } from "../../lib/logger.js";
import { icon } from "../../../shared/icons.js";
import { Filer } from "../../models/filer.js";
import type { ChatMessage, ChatStatus, MessagePart, SSEEvent, TextPart, ToolPart, ErrorCode } from "../../types/chat.js";
import { showToast } from "../toast/toast.js";

const log = createLogger("ft-chat");

// Import child components
import "./chat-input.js";
import "./chat-message.js";
import "./chat-suggestions.js";
import "./chat-tool-result.js";

if (!customElements.get("ft-chat")) {
  class FtChat extends HTMLElement {
    private messages: ChatMessage[] = [];
    private status: ChatStatus = "ready";
    private abortController: AbortController | null = null;
    private conversationEl: HTMLElement | null = null;
    private conversationInnerEl: HTMLElement | null = null;
    private titleTextEl: HTMLElement | null = null;
    private suggestionsEl: HTMLElement | null = null;
    private inputEl: HTMLElement | null = null;
    private scrollFabEl: HTMLButtonElement | null = null;
    private currentStreamingMessage: HTMLElement | null = null;
    private companyName: string | null = null;

    // Buffer-render loop state
    private renderLoopId: number | null = null;
    private pendingRender = false;
    private partsVersion = 0;

    constructor() {
      super();
      this.attachShadow({ mode: "open" });
    }

    static get observedAttributes(): string[] {
      return ["initial-query", "cik"];
    }

    connectedCallback(): void {
      log.debug("connected");
      this.render();
      this.setupReferences();
      this.setupEventListeners();
      this.initializeAndHandleQuery();
    }

    private async initializeAndHandleQuery(): Promise<void> {
      // Resolve company name from CIK (uses dedup cache)
      // Normalize to 10-digit zero-padded format expected by SEC API
      const rawCik = this.getAttribute("cik");
      const cik = rawCik ? rawCik.padStart(10, "0") : null;
      if (cik) {
        const filer = await Filer.find(cik);
        this.companyName = filer?.name || null;
        log.debug("resolved company", { cik, name: this.companyName });
      }

      // Handle initial query after company name is resolved
      const initialQuery = this.getAttribute("initial-query");
      if (initialQuery) {
        requestAnimationFrame(() => {
          this.handleSubmit(initialQuery);
        });
      }
    }

    disconnectedCallback(): void {
      log.debug("disconnected");
      this.stopRenderLoop();
      this.abortController?.abort();
    }

    private render(): void {
      const style = document.createElement("style");
      style.textContent = this.getStyles();

      const template = document.createElement("template");
      template.innerHTML = this.getTemplate();

      if (this.shadowRoot) {
        this.shadowRoot.appendChild(style);
        this.shadowRoot.appendChild(template.content.cloneNode(true));
      }
    }

    private getStyles(): string {
      return `
        :host {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0; /* Allow flex child to shrink */
          max-height: 100vh;
          max-height: 100dvh; /* Modern mobile: accounts for dynamic browser chrome */
          position: relative;
        }

        .chat-container {
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
        }

        /* Header - hidden in empty state */
        .header {
          display: none;
          align-items: center;
          padding: 12px 20px;
          border-bottom: 1px solid var(--card-border, #e5e7eb);
          background: var(--bg, #ffffff);
          position: sticky;
          top: 0;
          z-index: 5;
        }

        .chat-container:not(.empty) .header {
          display: flex;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sidebar-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          color: var(--muted, #6b7280);
        }

        .sidebar-toggle:hover {
          background: var(--surface, #f9fafb);
          color: var(--text, #1f2937);
        }

        .sidebar-toggle svg {
          width: 18px;
          height: 18px;
        }

        .chat-title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          font-weight: 500;
          color: var(--text, #1f2937);
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
          transition: background 0.15s ease;
        }

        .chat-title:hover {
          background: var(--surface, #f9fafb);
        }

        .chat-title .chevron {
          width: 14px;
          height: 14px;
          color: var(--muted, #6b7280);
        }

        .chat-title .chevron svg {
          width: 14px;
          height: 14px;
        }

        /* Empty state: center form vertically */
        .chat-container.empty {
          justify-content: center;
        }

        .chat-container.empty .conversation {
          flex: none;
          overflow: visible;
          padding: 0 16px 16px;
        }

        .chat-container.empty .input-area {
          padding-bottom: 20vh;
        }

        .chat-container.empty .input-area::before {
          display: none;
        }

        /* Conversation area */
        .conversation {
          flex: 1;
          overflow-y: auto;
          scroll-behavior: smooth;
        }

        .conversation-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 32px 24px 24px;
          width: 100%;
        }

        .chat-container.empty .conversation-inner {
          max-width: none;
          padding: 0;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          color: var(--muted, #6b7280);
          padding: 0 16px;
        }

        .empty-state h2 {
          font-size: 24px;
          font-weight: 600;
          color: var(--heading, #1f2937);
          margin: 0 0 8px;
        }

        .empty-state p {
          font-size: 14px;
          margin: 0 0 24px;
          max-width: 400px;
        }

        /* Input area - sticks to bottom */
        .input-area {
          flex-shrink: 0;
          position: relative;
          padding: 16px 24px 20px;
          background: var(--bg, #ffffff);
        }

        .input-area::before {
          content: "";
          position: absolute;
          bottom: 100%;
          left: 0;
          right: 0;
          height: 40px;
          background: linear-gradient(to top, var(--bg, #ffffff), transparent);
          pointer-events: none;
        }

        .input-wrapper {
          max-width: 720px;
          margin: 0 auto;
        }

        .disclaimer {
          text-align: center;
          font-size: 11px;
          color: var(--muted, #6b7280);
          margin-top: 8px;
        }

        /* Scroll to bottom FAB */
        .scroll-fab {
          position: absolute;
          bottom: 100px;
          left: 50%;
          transform: translateX(-50%) translateY(20px);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          padding: 0;
          background: var(--input-bg, #ffffff);
          border: 1px solid var(--card-border, #e5e7eb);
          border-radius: 50%;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          cursor: pointer;
          color: var(--muted, #6b7280);
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s ease;
          z-index: 11;
        }

        .scroll-fab:hover {
          background: var(--surface, #f9fafb);
          color: var(--text, #1f2937);
          transform: translateX(-50%) translateY(16px);
        }

        .scroll-fab.visible {
          opacity: 1;
          visibility: visible;
          transform: translateX(-50%) translateY(0);
        }

        .scroll-fab svg {
          width: 20px;
          height: 20px;
        }

        @media (prefers-color-scheme: dark) {
          .header {
            background: var(--bg, #0b0b0c);
            border-color: var(--card-border, #24262c);
          }

          .sidebar-toggle:hover {
            background: var(--surface, #14161a);
            color: var(--text, #f3f4f6);
          }

          .chat-title {
            color: var(--text, #f3f4f6);
          }

          .chat-title:hover {
            background: var(--surface, #14161a);
          }

          .input-area {
            background: var(--bg, #0b0b0c);
          }

          .input-area::before {
            background: linear-gradient(to top, var(--bg, #0b0b0c), transparent);
          }

          .empty-state {
            color: var(--muted, #a3a3a3);
          }

          .empty-state h2 {
            color: var(--heading, #ffffff);
          }

          .scroll-fab {
            background: var(--surface, #14161a);
            border-color: var(--card-border, #24262c);
            color: var(--muted, #a3a3a3);
          }

          .scroll-fab:hover {
            background: var(--tab-active-bg, #1a1c22);
            color: var(--text, #ffffff);
          }
        }
      `;
    }

    private getTemplate(): string {
      return `
        <div class="chat-container empty">
          <header class="header">
            <div class="header-left">
              <button class="sidebar-toggle" type="button" title="Toggle sidebar">
                ${icon("panel-left", 18)}
              </button>
              <span class="chat-title">
                <span class="title-text">New conversation</span>
                <span class="chevron">${icon("chevron-down", 14)}</span>
              </span>
            </div>
          </header>
          <div class="conversation">
            <div class="conversation-inner">
              <div class="empty-state">
                <h2>13F Filing Assistant</h2>
                <p>Ask questions about SEC 13F filings, portfolio holdings, and investment managers.</p>
                <ft-chat-suggestions></ft-chat-suggestions>
              </div>
            </div>
          </div>
          <button class="scroll-fab" type="button" aria-label="Scroll to bottom">
            ${icon("arrow-down", 20)}
          </button>
          <div class="input-area">
            <div class="input-wrapper">
              <ft-chat-input></ft-chat-input>
              <p class="disclaimer">AI can make mistakes. Please double-check responses.</p>
            </div>
          </div>
        </div>
      `;
    }

    private setupReferences(): void {
      this.conversationEl =
        this.shadowRoot?.querySelector(".conversation") ?? null;
      this.conversationInnerEl =
        this.shadowRoot?.querySelector(".conversation-inner") ?? null;
      this.titleTextEl =
        this.shadowRoot?.querySelector(".title-text") ?? null;
      this.suggestionsEl =
        this.shadowRoot?.querySelector("ft-chat-suggestions") ?? null;
      this.inputEl = this.shadowRoot?.querySelector("ft-chat-input") ?? null;
      this.scrollFabEl = this.shadowRoot?.querySelector(".scroll-fab") ?? null;
    }

    private setupEventListeners(): void {
      // Handle message submission from input
      this.shadowRoot?.addEventListener("chat-submit", ((e: CustomEvent) => {
        this.handleSubmit(e.detail.text);
      }) as EventListener);

      // Handle suggestion clicks
      this.shadowRoot?.addEventListener("suggestion-click", ((
        e: CustomEvent,
      ) => {
        this.handleSubmit(e.detail.text);
      }) as EventListener);

      // Scroll FAB visibility
      this.conversationEl?.addEventListener("scroll", () => {
        this.updateScrollFabVisibility();
      });

      // Scroll FAB click
      this.scrollFabEl?.addEventListener("click", () => {
        this.scrollToBottom();
      });
    }

    private updateScrollFabVisibility(): void {
      if (!this.conversationEl || !this.scrollFabEl) return;

      const { scrollTop, scrollHeight, clientHeight } = this.conversationEl;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Show FAB when scrolled up more than 100px from bottom
      if (distanceFromBottom > 100) {
        this.scrollFabEl.classList.add("visible");
      } else {
        this.scrollFabEl.classList.remove("visible");
      }
    }

    private async handleSubmit(text: string): Promise<void> {
      if (!text.trim() || this.status === "streaming") return;

      log.log("submit", { text: text.substring(0, 50) });

      // Transition from empty to active state
      const container = this.shadowRoot?.querySelector(".chat-container");
      const wasEmpty = container?.classList.contains("empty");
      container?.classList.remove("empty");

      // Set title from first user message (truncate to ~40 chars)
      if (wasEmpty && this.titleTextEl) {
        const title = text.length > 40 ? text.substring(0, 40) + "…" : text;
        this.titleTextEl.textContent = title;
      }

      // Hide empty state and suggestions
      const emptyState = this.shadowRoot?.querySelector(".empty-state");
      if (emptyState) {
        emptyState.remove();
      }

      // Add user message
      const userMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: "user",
        parts: [{ type: "text", text, state: "done" }],
      };
      this.messages.push(userMessage);
      this.renderMessage(userMessage);

      // Clear input
      const inputEl = this.inputEl as HTMLElement & { clear: () => void };
      inputEl?.clear?.();

      // Start streaming response
      await this.streamResponse();
    }

    // Helper to extract text content from parts for API
    private getMessageContent(message: ChatMessage): string {
      return message.parts
        .filter((p): p is TextPart => p.type === "text")
        .map((p) => p.text)
        .join("\n\n");
    }

    private async streamResponse(): Promise<void> {
      this.status = "submitted";
      this.updateInputStatus();

      // Create assistant message placeholder with empty parts
      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: "assistant",
        parts: [],
      };
      this.messages.push(assistantMessage);
      const messageEl = this.renderMessage(assistantMessage, true);
      this.currentStreamingMessage = messageEl;

      this.abortController = new AbortController();

      try {
        const rawCik = this.getAttribute("cik");
        const cik = rawCik ? rawCik.padStart(10, "0") : null;
        const response = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: this.messages.slice(0, -1).map((m) => ({
              role: m.role,
              content: this.getMessageContent(m),
            })),
            ...(cik && { cik, companyName: this.companyName }),
          }),
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        this.status = "streaming";
        this.updateInputStatus();
        this.startRenderLoop();

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No response body");

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const event: SSEEvent = JSON.parse(line.slice(6));
                this.handleSSEEvent(event, assistantMessage, messageEl);
              } catch (e) {
                log.error("SSE parse error", { line, error: e });
              }
            }
          }
        }

        // Mark all text parts as done
        for (const part of assistantMessage.parts) {
          if (part.type === "text") {
            part.state = "done";
          }
        }
        if (messageEl) {
          (messageEl as HTMLElement & { streaming: boolean }).streaming = false;
        }
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          log.debug("Request aborted");
        } else {
          log.error("Stream error", { error });
          const errorCode = this.getErrorCode((error as Error).message || "");
          showToast({
            message: this.getErrorDescription(errorCode),
            type: "error",
            duration: 0,
          });
        }
        // Remove empty assistant message on any error/abort
        messageEl?.remove();
        this.messages.pop();
      } finally {
        this.stopRenderLoop();
        this.status = "ready";
        this.updateInputStatus();
        this.abortController = null;
        this.currentStreamingMessage = null;
      }
    }

    private handleSSEEvent(
      event: SSEEvent,
      message: ChatMessage,
      messageEl: HTMLElement | null,
    ): void {
      switch (event.type) {
        case "text":
          if (event.text) {
            // Append to last text part, or create new one
            const lastPart = message.parts[message.parts.length - 1];
            if (lastPart?.type === "text") {
              lastPart.text += event.text;
            } else {
              message.parts.push({ type: "text", text: event.text, state: "streaming" });
            }
            this.scheduleRender();
          }
          break;

        case "tool_call":
          log.debug("tool_call", { name: event.name, id: event.id });
          if (event.name && event.id) {
            message.parts.push({
              type: "tool",
              id: event.id,
              name: event.name,
              status: "pending",
            });
            this.scheduleRender();
          }
          break;

        case "tool_result":
          log.debug("tool_result", { name: event.name, error: event.error });
          if (event.id) {
            const toolPart = message.parts.find(
              (p): p is ToolPart => p.type === "tool" && p.id === event.id
            );
            if (toolPart) {
              toolPart.status = event.error ? "error" : "success";
              toolPart.result = event.result;
              toolPart.error = event.error;
              this.scheduleRender();
            }
          }
          break;

        case "error": {
          log.error("SSE error", { message: event.message });
          const errorCode = this.getErrorCode(event.message || "");
          // Show persistent toast - user must dismiss
          showToast({
            message: this.getErrorDescription(errorCode),
            type: "error",
            duration: 0,
          });
          // Remove empty assistant message from DOM and state
          this.currentStreamingMessage?.remove();
          this.messages.pop();
          break;
        }

        case "done":
          log.debug("stream done");
          break;
      }
    }

    private renderMessage(
      message: ChatMessage,
      streaming = false,
    ): HTMLElement {
      const el = document.createElement("ft-chat-message");
      el.setAttribute("from", message.role);
      if (streaming && message.role === "assistant") {
        el.setAttribute("streaming", "");
      }

      (el as HTMLElement & { parts: MessagePart[] }).parts = message.parts;

      this.conversationInnerEl?.appendChild(el);
      this.scrollToBottom();

      return el;
    }

    private updateInputStatus(): void {
      if (this.inputEl) {
        (this.inputEl as HTMLElement & { status: ChatStatus }).status =
          this.status;
      }
    }

    /**
     * Check if user is scrolled to within 50px of bottom.
     * Used to determine if auto-scroll should occur during streaming.
     */
    private isUserAtBottom(): boolean {
      if (!this.conversationEl) return true;
      const { scrollTop, scrollHeight, clientHeight } = this.conversationEl;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      return distanceFromBottom <= 50;
    }

    /**
     * Scroll to bottom unconditionally (used for explicit user actions).
     */
    private scrollToBottom(): void {
      if (this.conversationEl) {
        requestAnimationFrame(() => {
          this.conversationEl!.scrollTop = this.conversationEl!.scrollHeight;
        });
      }
    }

    /**
     * Scroll to bottom only if user was already at bottom.
     * Preserves scroll position when user has scrolled up to read history.
     */
    private scrollToBottomIfNeeded(): void {
      if (this.isUserAtBottom()) {
        this.scrollToBottom();
      }
    }

    /**
     * Start the 60fps render loop for buffered SSE updates.
     * Decouples network ingestion from DOM painting.
     */
    private startRenderLoop(): void {
      if (this.renderLoopId !== null) return;

      const loop = () => {
        if (this.pendingRender && this.currentStreamingMessage) {
          const msg = this.messages[this.messages.length - 1];
          if (msg) {
            const el = this.currentStreamingMessage as HTMLElement & { version: number; parts: MessagePart[] };
            el.version = this.partsVersion;
            el.parts = [...msg.parts];
            this.scrollToBottomIfNeeded();
          }
          this.pendingRender = false;
        }
        this.renderLoopId = requestAnimationFrame(loop);
      };

      this.renderLoopId = requestAnimationFrame(loop);
    }

    /**
     * Stop the render loop when streaming ends.
     */
    private stopRenderLoop(): void {
      if (this.renderLoopId !== null) {
        cancelAnimationFrame(this.renderLoopId);
        this.renderLoopId = null;
      }
      // Flush any pending render
      if (this.pendingRender && this.currentStreamingMessage) {
        const msg = this.messages[this.messages.length - 1];
        if (msg) {
          const el = this.currentStreamingMessage as HTMLElement & { version: number; parts: MessagePart[] };
          el.version = this.partsVersion;
          el.parts = [...msg.parts];
        }
        this.pendingRender = false;
      }
    }

    /**
     * Mark that a render is needed. The render loop will apply it.
     */
    private scheduleRender(): void {
      this.partsVersion++;
      this.pendingRender = true;
    }

    /**
     * Extract error code from raw API error message.
     * Parses JSON to detect error type without exposing details.
     */
    private getErrorCode(rawMessage: string): ErrorCode {
      try {
        const jsonMatch = rawMessage.match(/\{.*\}/s);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          const errorType = parsed.error?.type || parsed.type;
          if (errorType === "invalid_request_error" && rawMessage.includes("credit")) {
            return "quota";
          }
          if (errorType === "rate_limit_error" || rawMessage.includes("429")) {
            return "rate_limit";
          }
          if (errorType === "overloaded_error" || rawMessage.includes("529")) {
            return "server";
          }
        }
      } catch {
        // Parsing failed
      }
      if (rawMessage.includes("5") && rawMessage.match(/5\d\d/)) {
        return "server";
      }
      return "unknown";
    }

    private getErrorDescription(code: ErrorCode): string {
      const descriptions: Record<ErrorCode, string> = {
        quota: "The AI assistant is temporarily unavailable. Please try again later.",
        rate_limit: "You've sent too many messages. Please wait a moment and try again.",
        server: "The server is experiencing issues. Please try again later.",
        network: "Unable to connect. Please check your internet connection.",
        unknown: "An unexpected error occurred. Please try again.",
      };
      return descriptions[code];
    }

    // Public API
    clear(): void {
      this.messages = [];
      this.partsVersion = 0;
      this.pendingRender = false;
      this.stopRenderLoop();

      // Restore empty state
      const container = this.shadowRoot?.querySelector(".chat-container");
      container?.classList.add("empty");

      // Reset title
      if (this.titleTextEl) {
        this.titleTextEl.textContent = "New conversation";
      }

      if (this.conversationInnerEl) {
        this.conversationInnerEl.innerHTML = `
          <div class="empty-state">
            <h2>13F Filing Assistant</h2>
            <p>Ask questions about SEC 13F filings, portfolio holdings, and investment managers.</p>
            <ft-chat-suggestions></ft-chat-suggestions>
          </div>
        `;
      }
    }

    abort(): void {
      this.abortController?.abort();
    }
  }

  customElements.define("ft-chat", FtChat);
}
